import { useEffect, useState } from "react";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { invalidateEmailLogsAfterDelay } from "./emailLogsUtility";
import {
  constructCancelEmailEndpoint,
  constructEmailLogDetailEndpoint,
  constructRetryEmailEndpoint,
} from "../../../../../common/infrastructure/routes/endpointsHelpers";
import {
  IEmailLogDetailModel,
  IEmailLogModel,
  IPaginatedResponse,
} from "../models/emailLogModel";

const PAGE_SIZE = 20;

interface IUseEmailLogsListParams {
  filterUserIds: number[] | null;
  filterEmailTypes: number[] | null;
}

export const useEmailLogsList = ({ filterUserIds, filterEmailTypes }: IUseEmailLogsListParams) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);

  useEffect(() => {
    setPage(1);
  }, [filterUserIds, filterEmailTypes]);

  const listParams = new URLSearchParams({ page: page.toString(), pageSize: PAGE_SIZE.toString() });
  filterUserIds?.forEach((id) => listParams.append("userIds", id.toString()));
  filterEmailTypes?.forEach((t) => listParams.append("emailTypes", t.toString()));
  const listEndpoint = `${endpointsMetadata.communicationLogs.getEmailLogs}?${listParams.toString()}`;

  const { data, isLoading } = useApiQuery<IPaginatedResponse<IEmailLogModel>>({
    queryKey: [endpointsMetadata.communicationLogs.getEmailLogs, page, filterUserIds, filterEmailTypes],
    endpoint: listEndpoint,
    globalIsLoading: false,
  });

  const { data: detail, isLoading: isDetailLoading } =
    useApiQuery<IEmailLogDetailModel>({
      queryKey: [constructEmailLogDetailEndpoint(selectedEmailId ?? 0)],
      endpoint: constructEmailLogDetailEndpoint(selectedEmailId ?? 0),
      enabled: !!selectedEmailId,
      globalIsLoading: false,
    });

  const { mutate: cancelEmail, isLoading: isCancelling } = useApiMutation({
    onSuccess: () => setSelectedEmailId(null),
  });

  const { mutate: retryEmail, isLoading: isRetrying } = useApiMutation({
    onSuccess: () => {
      setSelectedEmailId(null);
      queryClient.invalidateQueries({ queryKey: [endpointsMetadata.communicationLogs.getEmailLogs] });
      invalidateEmailLogsAfterDelay(queryClient, 3000);
    },
  });

  const handlePageChange = (e: PaginatorPageChangeEvent) => {
    setPage(Math.floor(e.first / PAGE_SIZE) + 1);
  };

  const handleCancel = () => {
    if (!selectedEmailId) return;
    cancelEmail({
      endpoint: constructCancelEmailEndpoint(selectedEmailId),
      method: "put",
      payload: {},
      successMessage: "Имейлът е отменен.",
      invalidateKeys: [[endpointsMetadata.communicationLogs.getEmailLogs, page, filterUserIds, filterEmailTypes]],
    });
  };

  const handleRetry = () => {
    if (!selectedEmailId) return;
    retryEmail({
      endpoint: constructRetryEmailEndpoint(selectedEmailId),
      method: "put",
      payload: {},
      successMessage: "Имейлът е поставен за повторно изпращане.",
    });
  };

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  return {
    page,
    items,
    totalCount,
    isLoading,
    detail,
    isDetailLoading,
    isCancelling,
    isRetrying,
    selectedEmailId,
    setSelectedEmailId,
    handlePageChange,
    handleCancel,
    handleRetry,
    PAGE_SIZE,
  };
};
